/**
 * CSV Validator Service
 *
 * Validates parsed CSV data against business rules before import.
 * Checks required fields, data types, relationships, and duplicates.
 *
 * @module ImportExportTab/services/csvValidator
 */

import { ORGUNIT_RULES, LOCATION_RULES } from '../utils/validationRules';

/**
 * Validate OrgUnit CSV data
 *
 * Validates each record against ORGUNIT_RULES, checks for duplicates,
 * and validates parent-child relationships.
 *
 * @param {Object[]} data - Parsed CSV data array
 * @param {Object[]} existingOrgUnits - Existing OrgUnits from database
 * @returns {Object} Validation result
 * @returns {Object[]} result.validRecords - Records that passed validation
 * @returns {Object[]} result.invalidRecords - Records with errors (includes rowNumber and errors array)
 * @returns {Object} result.summary - Summary statistics
 *
 * @example
 * const result = validateOrgUnits(csvData, existingOrgUnits);
 * console.log(`Valid: ${result.summary.validCount}, Invalid: ${result.summary.invalidCount}`);
 */
export function validateOrgUnits(data, existingOrgUnits = []) {
  const validRecords = [];
  const invalidRecords = [];

  // Detect duplicates within CSV data
  const duplicateNames = detectDuplicates(data, 'name');

  // Create lookup map of all OrgUnit names (existing + CSV data)
  const allOrgUnitNames = new Set([
    ...existingOrgUnits.map(ou => ou.name),
    ...data.map(row => row.name).filter(name => name && name.trim())
  ]);

  data.forEach((record, index) => {
    const rowNumber = index + 2; // 1-indexed, +1 for header row
    const errors = [];

    // Validate required fields
    errors.push(...validateRequired(record, ORGUNIT_RULES));

    // Validate data types
    errors.push(...validateDataTypes(record, ORGUNIT_RULES));

    // Check for duplicates
    if (duplicateNames.has(record.name)) {
      errors.push({
        field: 'name',
        message: `Duplicate OrgUnit name "${record.name}" found in CSV`
      });
    }

    // Validate parent-child relationship
    if (record.parentOrgUnitName && record.parentOrgUnitName.trim()) {
      // Parent must exist in either existing OrgUnits or earlier in CSV
      if (!allOrgUnitNames.has(record.parentOrgUnitName)) {
        errors.push({
          field: 'parentOrgUnitName',
          message: `Parent OrgUnit "${record.parentOrgUnitName}" does not exist`
        });
      }
    }

    // Categorize record
    if (errors.length === 0) {
      validRecords.push(record);
    } else {
      invalidRecords.push({
        ...record,
        rowNumber,
        errors
      });
    }
  });

  return {
    validRecords,
    invalidRecords,
    summary: {
      totalRecords: data.length,
      validCount: validRecords.length,
      invalidCount: invalidRecords.length
    }
  };
}

/**
 * Validate Location CSV data
 *
 * Validates each record against LOCATION_RULES, checks for duplicates,
 * and validates OrgUnit references.
 *
 * @param {Object[]} data - Parsed CSV data array
 * @param {Object[]} existingLocations - Existing Locations from database
 * @param {Object[]} existingOrgUnits - Existing OrgUnits from database
 * @returns {Object} Validation result
 * @returns {Object[]} result.validRecords - Records that passed validation
 * @returns {Object[]} result.invalidRecords - Records with errors
 * @returns {Object} result.summary - Summary statistics
 *
 * @example
 * const result = validateLocations(csvData, existingLocations, existingOrgUnits);
 */
export function validateLocations(data, existingLocations = [], existingOrgUnits = []) {
  const validRecords = [];
  const invalidRecords = [];

  // Detect duplicates within CSV data
  const duplicateNames = detectDuplicates(data, 'locationName');

  // Create lookup set of existing ORGLEVEL_1 OrgUnit names
  const orgLevel1Names = new Set(
    existingOrgUnits
      .filter(ou => ou.orgLevel === 'ORGLEVEL_1')
      .map(ou => ou.name)
  );

  data.forEach((record, index) => {
    const rowNumber = index + 2; // 1-indexed, +1 for header row
    const errors = [];

    // Validate required fields
    errors.push(...validateRequired(record, LOCATION_RULES));

    // Validate data types
    errors.push(...validateDataTypes(record, LOCATION_RULES));

    // Check for duplicates
    if (duplicateNames.has(record.locationName)) {
      errors.push({
        field: 'locationName',
        message: `Duplicate location name "${record.locationName}" found in CSV`
      });
    }

    // Validate orgLevel_1_name references existing OrgUnit
    if (record.orgLevel_1_name && record.orgLevel_1_name.trim()) {
      if (!orgLevel1Names.has(record.orgLevel_1_name)) {
        errors.push({
          field: 'orgLevel_1_name',
          message: `OrgUnit "${record.orgLevel_1_name}" does not exist or is not ORGLEVEL_1`
        });
      }
    }

    // Categorize record
    if (errors.length === 0) {
      validRecords.push(record);
    } else {
      invalidRecords.push({
        ...record,
        rowNumber,
        errors
      });
    }
  });

  return {
    validRecords,
    invalidRecords,
    summary: {
      totalRecords: data.length,
      validCount: validRecords.length,
      invalidCount: invalidRecords.length
    }
  };
}

/**
 * Validate required fields
 *
 * Checks that all required fields are present and not empty.
 *
 * @param {Object} record - Single CSV record
 * @param {Object} rules - Validation rules object
 * @returns {Object[]} Array of error objects
 *
 * @example
 * const errors = validateRequired(record, ORGUNIT_RULES);
 */
export function validateRequired(record, rules) {
  const errors = [];

  Object.keys(rules).forEach(field => {
    const rule = rules[field];
    if (rule.required) {
      const value = record[field];
      if (value === undefined || value === null || String(value).trim() === '') {
        errors.push({
          field,
          message: rule.errorMessage || `${field} is required`
        });
      }
    }
  });

  return errors;
}

/**
 * Validate data types using field validators
 *
 * Runs validator function for each field and collects errors.
 *
 * @param {Object} record - Single CSV record
 * @param {Object} rules - Validation rules object
 * @returns {Object[]} Array of error objects
 *
 * @example
 * const errors = validateDataTypes(record, LOCATION_RULES);
 */
export function validateDataTypes(record, rules) {
  const errors = [];

  Object.keys(rules).forEach(field => {
    const rule = rules[field];
    const value = record[field];

    // Skip validation if field is empty and not required
    if (!rule.required && (value === undefined || value === null || String(value).trim() === '')) {
      return;
    }

    // Run validator function
    if (rule.validator && !rule.validator(value)) {
      errors.push({
        field,
        message: rule.errorMessage || `${field} has invalid value`
      });
    }
  });

  return errors;
}

/**
 * Detect duplicate values in key field
 *
 * Returns a Set of values that appear more than once in the data.
 *
 * @param {Object[]} data - Array of records
 * @param {string} keyField - Field name to check for duplicates
 * @returns {Set<string>} Set of duplicate values
 *
 * @example
 * const duplicates = detectDuplicates(data, 'name');
 * if (duplicates.has('Duplicate Name')) {
 *   console.log('Found duplicate');
 * }
 */
export function detectDuplicates(data, keyField) {
  const seen = new Map();
  const duplicates = new Set();

  data.forEach(record => {
    const value = record[keyField];
    if (value && String(value).trim()) {
      const normalized = String(value).trim();
      if (seen.has(normalized)) {
        duplicates.add(normalized);
      } else {
        seen.set(normalized, true);
      }
    }
  });

  return duplicates;
}
